import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Map, Users, Settings, PencilRuler, FileText } from "lucide-react";
import useProject from "@/hooks/useProject";
import { useActiveProject } from "@/context/ProjectContext";
import Navbar from "@/components/Navbar";
import ProjectRoadmap from "@/components/ProjectRoadmap";
import MembersManager from "@/components/MembersManager";
import ProjectSettingsForm from "@/components/ProjectSettingsForm";
import Designs from "@/pages/admin/Designs";
import MoriAIChat from "@/components/MoriAIChat";
import LoadingScreen from "@/components/LoadingScreen";
import NoAccess from "@/pages/NoAccess";
import HokusaiDecor from "@/components/HokusaiDecor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { printPresupuesto } from "@/lib/presupuestoPrint";

const TABS = [
  { key: "hoja-de-ruta", label: "Hoja de ruta", icon: Map },
  { key: "disenos", label: "Diseños", icon: PencilRuler },
  { key: "usuarios", label: "Usuarios", icon: Users },
  { key: "ajustes", label: "Ajustes", icon: Settings },
];

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { project, setProject, tasks, setTasks, phases, loading, error, refreshProject } = useProject(projectId);
  const { activeProject, setActiveProject } = useActiveProject();
  const [selectedId, setSelectedId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (phases.length && !selectedId) setSelectedId(phases[0].id);
  }, [phases, selectedId]);

  // Mantener sincronizado el "proyecto activo" global al abrir su espacio.
  useEffect(() => {
    if (project?.project_id && activeProject?.project_id !== project.project_id) {
      setActiveProject(project);
    }
  }, [project, activeProject, setActiveProject]);

  const activePhase = useMemo(() => phases.find((p) => p.id === selectedId), [phases, selectedId]);
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "hoja-de-ruta";

  if (loading) return <LoadingScreen text="Cargando el proyecto…" />;
  if (error || !project) return <NoAccess title="No tienes acceso a este proyecto" backTo="/admin" />;
  if (project.my_role !== "administrador") return <NoAccess title="Este espacio es solo para administradores del proyecto" backTo="/" />;

  return (
    <div className="relative min-h-screen bg-background" data-testid="project-workspace">
      <HokusaiDecor />
      <Navbar
        backTo="/admin"
        title={project.name}
        subtitle={TABS.find((t) => t.key === tab)?.label || project.location}
        roleLabel="Administrador"
        globalPercent={project.progress?.global_percent}
        onOpenChat={() => setChatOpen(true)}
        cover={project.cover_path}
      />
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <TabsList className="h-11 rounded-full p-1 bg-secondary" data-testid="workspace-tabs">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} className="rounded-full gap-2 px-4 h-9" data-testid={`tab-${t.key}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <Button variant="outline" className="rounded-full gap-2" onClick={() => printPresupuesto(projectId)} data-testid="presupuesto-pdf-button">
              <FileText className="h-4 w-4" /> Presupuesto PDF
            </Button>
          </div>
          <TabsContent value="hoja-de-ruta">
            <ProjectRoadmap
              project={project}
              tasks={tasks}
              setTasks={setTasks}
              phases={phases}
              refreshProject={refreshProject}
              canEdit
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </TabsContent>
          <TabsContent value="disenos">
            <Designs projectId={projectId} />
          </TabsContent>
          <TabsContent value="usuarios">
            <MembersManager projectId={projectId} />
          </TabsContent>
          <TabsContent value="ajustes">
            <ProjectSettingsForm project={project} onSaved={setProject} onDeleted={() => navigate("/admin")} />
          </TabsContent>
        </Tabs>
      </main>
      <MoriAIChat open={chatOpen} onOpenChange={setChatOpen} activePhase={activePhase} projectId={projectId} />
    </div>
  );
}
